import styled from 'styled-components';
import { MatchExtra as Extra, MatchLineup } from '../api/client';
import { H2, Muted } from './ui';

// Sección rica de un partido con datos de ESPN: estadísticas comparativas, alineaciones,
// línea de eventos y ficha técnica (árbitro + asistencia). Se muestra solo si available.
export function MatchExtra({
  data,
  team1,
  team2,
}: {
  data: Extra;
  team1: string;
  team2: string;
}) {
  if (!data.available) return null;
  const stats = data.stats ?? [];
  const events = data.events ?? [];
  const l1 = data.lineups?.team1 ?? null;
  const l2 = data.lineups?.team2 ?? null;

  return (
    <>
      {(data.referee || data.attendance) && (
        <Tecnica>
          {data.referee && (
            <span>
              🧑‍⚖️ <strong>{data.referee}</strong>
            </span>
          )}
          {data.attendance != null && data.attendance > 0 && (
            <span>
              👥 {data.attendance.toLocaleString('es-AR')}
            </span>
          )}
        </Tecnica>
      )}

      {stats.length > 0 && (
        <>
          <H2>Estadísticas</H2>
          {stats.map((s) => (
            <StatRow key={s.label} a={num(s.team1)} b={num(s.team2)}>
              <div className="vals">
                <span>{s.team1}</span>
                <span className="lbl">{s.label}</span>
                <span>{s.team2}</span>
              </div>
              <div className="bar">
                <div className="a" />
                <div className="b" />
              </div>
            </StatRow>
          ))}
        </>
      )}

      {(l1 || l2) && (
        <>
          <H2>Alineaciones</H2>
          <Lineup name={team1} l={l1} />
          <Lineup name={team2} l={l2} />
        </>
      )}

      {events.length > 0 && (
        <>
          <H2>Eventos</H2>
          {events.map((e, i) => (
            <Ev key={i}>
              <span className="min">{e.clock}</span>
              <span className="tx">{e.text}</span>
            </Ev>
          ))}
        </>
      )}
    </>
  );
}

function Lineup({ name, l }: { name: string; l: MatchLineup | null }) {
  if (!l) return null;
  return (
    <LineupBox>
      <div className="hd">
        {name}
        {l.formation && <span className="fm">{l.formation}</span>}
      </div>
      <div className="players">
        {l.starters.map((p, i) => (
          <div className="p" key={i}>
            <span className="num">{p.num ?? ''}</span>
            <span className="pos">{p.pos}</span>
            <span className="nm">{p.name}</span>
          </div>
        ))}
      </div>
      {l.bench.length > 0 && (
        <Muted style={{ fontSize: '0.82rem', marginTop: 6 }}>
          Suplentes: {l.bench.map((p) => p.name).join(', ')}
        </Muted>
      )}
    </LineupBox>
  );
}

// Extrae el primer número de un string ("62%" → 62, "10" → 10) para dibujar la barra.
function num(v: string): number {
  const m = v.match(/[\d.]+/);
  return m ? parseFloat(m[0]) : 0;
}

const Tecnica = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  justify-content: center;
  margin: 12px 0 4px;
  font-size: 0.92rem;
  color: ${({ theme }) => theme.colors.textMuted};
  strong { color: ${({ theme }) => theme.colors.text}; }
`;

const StatRow = styled.div<{ a: number; b: number }>`
  margin-bottom: 12px;
  .vals {
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 0.95rem;
    margin-bottom: 4px;
  }
  .vals .lbl {
    color: ${({ theme }) => theme.colors.textMuted};
    font-weight: 600;
  }
  .bar {
    display: flex;
    height: 7px;
    border-radius: 999px;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.surfaceAlt};
  }
  .bar .a {
    flex: ${({ a, b }) => (a + b > 0 ? a / (a + b) : 0.5)};
    background: ${({ theme }) => theme.colors.accent};
  }
  .bar .b {
    flex: ${({ a, b }) => (a + b > 0 ? b / (a + b) : 0.5)};
    background: ${({ theme }) => theme.colors.gold};
  }
`;

const LineupBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 10px;
  .hd {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    margin-bottom: 8px;
  }
  .hd .fm {
    font-size: 0.78rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border-radius: 999px;
    padding: 2px 9px;
  }
  .players { display: flex; flex-direction: column; gap: 4px; }
  .p { display: flex; align-items: center; gap: 8px; font-size: 0.92rem; }
  .p .num {
    min-width: 22px;
    text-align: center;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.colors.accent};
  }
  .p .pos {
    min-width: 34px;
    font-size: 0.72rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Ev = styled.div`
  display: flex;
  gap: 10px;
  padding: 6px 2px;
  border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: 0.92rem;
  .min {
    min-width: 38px;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.accent};
    font-variant-numeric: tabular-nums;
  }
`;
